import { Box, Drawer, Grid, IconButton, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { groupBy } from 'lodash';
import { TApplyVasPayload } from '@/services/cart/models/update-cart';
import { Dictionary } from '@reduxjs/toolkit';
import { useCart } from '@/hooks/useCart';
import { ApiState } from '@/models/generics';
import {
	CART_TYPE,
	TCartItemApplicableValueAddedServices,
} from '@/services/cart/models/cart';
import {
	ICartItem,
	VAS_TYPES,
	TCartValueAddedServices,
} from '@/services/cart/models/cart';
import VasCustomizationConfirmation from './VasCustomizationConfirmation';
import VasCustomization from './VasCustomization';
import CloseIcon from '@/assets/CloseIcon.svg';
import FlexBox from '@/atomic-components/FlexBox';
import { useFullScreenLoader } from '@/hooks/useFullScreenLoader';
import { LencoTypography } from '@/atomic-components/LencoTypography';
import { emitMoeEvent } from '@/utils/dataLayer';

const vasTypes = ['FURLENCO_CARE_PROGRAM', 'FLEXI_CANCELLATION'];

interface ICartVasCustomizationPopupProps {
	cartType: CART_TYPE;
	items: ICartItem[];
	selectedVasToEdit?: TCartValueAddedServices;
	setSelectedVasToEdit: (selectedVas?: TCartValueAddedServices) => void;
	onClose: () => void;
}

export type DSelectedItemsForParticularVasType = Record<
	VAS_TYPES,
	Dictionary<ICartItem>
>;

export type DVasType_productsGroupByVasId = Record<
	VAS_TYPES,
	Dictionary<ICartItem[]>
>;

const CartVasCustomizationPopup = (props: ICartVasCustomizationPopupProps) => {
	const { cartActions } = useCart();
	const { loader } = useFullScreenLoader();
	const [selectedItemsForSelectedVasType, setSelectedItemsForSelectedVasType] =
		useState<DSelectedItemsForParticularVasType>({
			FURLENCO_CARE_PROGRAM: {},
			FLEXI_CANCELLATION: {},
		});
	const [selectedItemsChanged, setSelectedItemsChanged] =
		useState<boolean>(false);
	const [
		cartItemsGroupedByVasTypeAndVasID,
		setCartItemsGroupedByVasTypeAndVasID,
	] = useState<DVasType_productsGroupByVasId>({
		FURLENCO_CARE_PROGRAM: {},
		FLEXI_CANCELLATION: {},
	});
	const [itemsCountByVasType, setItemsCountByVasType] =
		useState<Record<string, number>>();
	const [applyVasViewState, setApplyVasViewState] = useState<ApiState>(
		ApiState.IDLE
	);
	const applyingVas = applyVasViewState === ApiState.LOADING;
	const vas = props.selectedVasToEdit;
	const isVasDrawerOpen = !!props.selectedVasToEdit;

	const itemsCountForSelectedVas = itemsCountByVasType
		? itemsCountByVasType[props.selectedVasToEdit?.type as string]
		: 0;

	// below contains array of items, belonging to a particular type of vas id, grouped togethor
	const itemsGroupedByVasID = useMemo(
		() =>
			Object.values(
				cartItemsGroupedByVasTypeAndVasID![
					props.selectedVasToEdit?.type as keyof DVasType_productsGroupByVasId
				] ?? {}
			),
		[cartItemsGroupedByVasTypeAndVasID, props.selectedVasToEdit]
	);

	const disableConfirmButton = !selectedItemsChanged || applyingVas;

	const vasTypeIndex = useMemo(
		() => vasTypes.indexOf(vas?.type as string) + 1,
		[vas?.type]
	);

	const allItemsSelected = useMemo(() => {
		return itemsGroupedByVasID.every((items) => {
			const isEvery = items?.every((item) => {
				if (
					selectedItemsForSelectedVasType![
						vas?.type as keyof DSelectedItemsForParticularVasType
					][item.id]
				) {
					return true;
				}
			});

			return isEvery;
		});
	}, [itemsGroupedByVasID, selectedItemsForSelectedVasType, vas?.type]);

	function prepareData() {
		const selectedItemsClone = { ...selectedItemsForSelectedVasType };
		const vasTypeGroupedProducts: DVasType_productsGroupByVasId = {
			FURLENCO_CARE_PROGRAM: {},
			FLEXI_CANCELLATION: {},
		};

		let totalItemsByVasTypes: Record<string, number> = {
			FURLENCO_CARE_PROGRAM: 0,
			FLEXI_CANCELLATION: 0,
		};

		vasTypes.forEach((vasType) => {
			const grouptedCartItems = groupBy(props.items, (item) => {
				totalItemsByVasTypes[vasType]++;
				const particularVasTypeForItem =
					item.valueAddedServices[
						vasType as keyof TCartItemApplicableValueAddedServices
					];
				const vasId = particularVasTypeForItem?.id;

				if (!!particularVasTypeForItem?.selected) {
					selectedItemsClone[
						vasType as keyof DSelectedItemsForParticularVasType
					]![item.id as number] = item;
				}

				return vasId;
			});

			vasTypeGroupedProducts[vasType as keyof DVasType_productsGroupByVasId] =
				grouptedCartItems;
		});

		setItemsCountByVasType(totalItemsByVasTypes);

		setSelectedItemsForSelectedVasType(selectedItemsClone);
		setCartItemsGroupedByVasTypeAndVasID(vasTypeGroupedProducts);
	}

	function closeCartVasCustomizationPopup() {
		props.setSelectedVasToEdit(undefined);
	}

	function selectItem(item: ICartItem) {
		const cloned = { ...selectedItemsForSelectedVasType };

		if (
			cloned[vas?.type as keyof DSelectedItemsForParticularVasType]![item.id]
		) {
			delete cloned[vas?.type as keyof DSelectedItemsForParticularVasType]![
				item.id
			];
		} else {
			cloned[vas?.type as keyof DSelectedItemsForParticularVasType]![item.id] =
				item;
		}

		setSelectedItemsForSelectedVasType(cloned);
		setSelectedItemsChanged(true);
	}

	function selectAllItems() {
		const cloned = { ...selectedItemsForSelectedVasType };
		const allItemsSelectedClone = allItemsSelected;

		itemsGroupedByVasID.forEach((items) => {
			items?.forEach((item) => {
				if (allItemsSelectedClone) {
					delete cloned[vas?.type as keyof DSelectedItemsForParticularVasType]![
						item.id
					];
				} else {
					cloned[vas?.type as keyof DSelectedItemsForParticularVasType]![
						item.id
					] = item;
				}
			});
		});

		setSelectedItemsForSelectedVasType(cloned);
		setSelectedItemsChanged(true);
	}

	function publishVasConfirmEvent() {
		// dataLayerPushWithEvent('moe_event', {
		// 	moe_event_name: 'VAS Products Confirmed',
		// 	moe_event_data: {
		// 		VAS_type: props.selectedVasToEdit?.value?.type,
		// 	},
		// });
	}

	function onVasApplySuccess() {
		closeCartVasCustomizationPopup();
	}

	function onVasApplyCompleted() {
		setApplyVasViewState(ApiState.IDLE);
		loader.hide();
	}

	function onApplyVasConfirmation() {
		loader.show();
		emitMoeEvent('VAS Products Confirmed');
		const itemId_vasTypesGroups: Record<number, Dictionary<boolean>> = {};

		const payload: TApplyVasPayload = {
			updatedCartItems: [],
		};

		for (const vasType in selectedItemsForSelectedVasType) {
			const itemsMap = selectedItemsForSelectedVasType[vasType as VAS_TYPES];

			Object.values(itemsMap ?? {}).map((item) => {
				if (!!item?.id) {
					if (!itemId_vasTypesGroups[item.id!]) {
						itemId_vasTypesGroups[item.id] = {};
					}

					itemId_vasTypesGroups[item.id][vasType] = true;
				}
			});
		}

		props.items.forEach((item) => {
			const vasMapsForItem = itemId_vasTypesGroups[item.id] ?? {};
			const appliedVasGroupForItem = Object.keys(vasMapsForItem);

			payload.updatedCartItems.push({
				cartItemId: Number(item.id),
				valueAddedServices: appliedVasGroupForItem as string[],
			});
		});

		setApplyVasViewState(ApiState.LOADING);

		cartActions
			.applyVas(props.cartType, payload)
			.then(onVasApplySuccess)
			.finally(onVasApplyCompleted);
	}

	useEffect(() => {
		if (isVasDrawerOpen) {
			emitMoeEvent('VAS Edit Page Opened');
		}
		if (!applyingVas) {
			prepareData();
		}
	}, [props.selectedVasToEdit]);

	return (
		<Drawer
			anchor={'right'}
			open={isVasDrawerOpen}
			onClose={props.onClose}
			variant='temporary'
			PaperProps={{
				sx: {
					width: '460px',
					justifyContent: 'flex-start',
					alignItems: 'center',
				},
			}}
		>
			<Grid
				container
				mt='40px'
				p='40px'
				justifyContent='space-evenly'
				alignContent='flex-start'
				sx={{ width: '100%', maxWidth: '1440px' }}
			>
				<Grid xs={12}>
					<FlexBox
						mb='16px'
						justifyContent={'space-between'}
						alignItems='center'
					>
						<LencoTypography variant='H11_Medium'>
							Value Added Services
						</LencoTypography>
						<IconButton onClick={props.onClose}>
							<CloseIcon />
						</IconButton>
					</FlexBox>
				</Grid>
				<Grid item xs={12}>
					<VasCustomization
						vasTypeIndex={vasTypeIndex}
						vas={vas}
						itemsGroupedByVasID={itemsGroupedByVasID}
						selectItem={selectItem}
						selectedItemsForSelectedVasType={selectedItemsForSelectedVasType}
					/>
				</Grid>
			</Grid>
			<Box
				sx={{
					width: '100%',
					position: 'sticky',
					bottom: '0px',
					left: '0px',
				}}
			>
				<VasCustomizationConfirmation
					selectAllItems={selectAllItems}
					allItemsSelected={allItemsSelected}
					onConfirm={onApplyVasConfirmation}
					disableConfirmButton={disableConfirmButton!}
					applyingVas={applyingVas}
					itemsCount={itemsCountForSelectedVas}
				/>
			</Box>
		</Drawer>
	);
};

export default CartVasCustomizationPopup;
